/**
 * Leitura dos erros estruturados que o backend devolve no módulo de formulários (T8a).
 *
 * O corpo vem em `response.data.data` (o mesmo lugar que o `showError` já usa):
 * - 400 `FORMULARIO_INVALIDO` → `erros[]` com `campo`, `codigoCampo`, `caminho`, `codigo` e `mensagem`;
 * - 409 `FORMULARIO_BLOQUEADO` → `motivos[]`;
 * - 400 `RESPOSTAS_INVALIDAS` → `codigosFaltantes[]` e `codigosInvalidos[]` (só códigos, nunca valores).
 *
 * Erro sem destino mapeável continua indo para o `showError`, para nunca ser engolido.
 */
export const CODIGO_FORMULARIO_INVALIDO = 'FORMULARIO_INVALIDO'
export const CODIGO_FORMULARIO_BLOQUEADO = 'FORMULARIO_BLOQUEADO'
export const CODIGO_RESPOSTAS_INVALIDAS = 'RESPOSTAS_INVALIDAS'

/** `campo` do erro: atributo do formulário com problema. */
export type CampoComErro = 'codigo' | 'condicaoExibicao' | 'opcoes' | 'ordem' | string

export type ErroFormulario = {
  campo?: CampoComErro | null
  codigoCampo?: string | null
  caminho?: string | null
  codigo?: string | null
  mensagem?: string | null
}

export type MotivoBloqueio = {
  codigo?: string | null
  mensagem?: string | null
}

export type ErrosFormulario = {
  code: string
  message: string
  erros: ErroFormulario[]
  motivos: MotivoBloqueio[]
  codigosFaltantes: string[]
  codigosInvalidos: string[]
}

const listaDeTextos = (valor: unknown): string[] =>
  Array.isArray(valor) ? valor.filter((item): item is string => typeof item === 'string') : []

/** `null` quando a resposta não é um erro estruturado do módulo (rede, 500, 403 etc.). */
export const extrairErrosFormulario = (error: unknown): ErrosFormulario | null => {
  const corpo = (error as { response?: { data?: { data?: unknown } } })?.response?.data?.data
  if (!corpo || typeof corpo !== 'object') return null

  const dados = corpo as Record<string, unknown>
  const code = typeof dados.code === 'string' ? dados.code : ''
  if (!code) return null

  return {
    code,
    message: typeof dados.message === 'string' ? dados.message : '',
    erros: Array.isArray(dados.erros) ? (dados.erros as ErroFormulario[]) : [],
    motivos: Array.isArray(dados.motivos) ? (dados.motivos as MotivoBloqueio[]) : [],
    codigosFaltantes: listaDeTextos(dados.codigosFaltantes),
    codigosInvalidos: listaDeTextos(dados.codigosInvalidos),
  }
}

export const ehFormularioBloqueado = (error: unknown): boolean =>
  extrairErrosFormulario(error)?.code === CODIGO_FORMULARIO_BLOQUEADO

/** Erros do atributo pedido (ex.: todos os de `condicaoExibicao` de um campo). */
export const errosDoAtributo = (
  erros: ErroFormulario[],
  campo: CampoComErro,
  codigoCampo?: string | null
): ErroFormulario[] =>
  erros.filter(
    (erro) => erro.campo === campo && (codigoCampo == null || erro.codigoCampo === codigoCampo)
  )

/** Primeira mensagem para um atributo, ou `undefined` quando não há erro dele. */
export const mensagemDoAtributo = (
  erros: ErroFormulario[],
  campo: CampoComErro,
  codigoCampo?: string | null
): string | undefined => errosDoAtributo(erros, campo, codigoCampo)[0]?.mensagem ?? undefined
